import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Linkedin, Twitter } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TOPICS = [
  { label: "Newcomer Essentials", href: "/topics/newcomers" },
  { label: "Budgeting", href: "/topics/budgeting" },
  { label: "Credit Building", href: "/topics/credit-building" },
  { label: "Mortgages", href: "/topics/mortgages" },
  { label: "Insurance", href: "/topics/insurance" },
  { label: "Cost of Living", href: "/topics/cost-of-living" },
];

const SOCIAL = [
  { icon: Youtube, href: "https://youtube.com", label: "YouTube" },
  { icon: Instagram, href: "https://instagram.com", label: "Instagram" },
  { icon: Twitter, href: "https://x.com", label: "X / Twitter" },
  { icon: Linkedin, href: "https://linkedin.com", label: "LinkedIn" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/subscribe`, { email });
      setStatus({ type: "success", msg: res.data.message });
      setEmail("");
    } catch (err) {
      setStatus({ type: "error", msg: err.response?.data?.detail || "Something went wrong." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#1B4332] text-white" data-testid="site-footer">
      <div className="max-w-screen-xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-3 gap-12">
        {/* Brand */}
        <div className="space-y-4">
          <div>
            <p className="text-[#D4A373] text-xs tracking-[0.2em] uppercase font-semibold">
              Straight Talk Money
            </p>
            <h3 className="text-white font-bold text-xl leading-tight">with Michael</h3>
          </div>
          <p className="text-[#A8C5B5] text-sm leading-relaxed">
            Research-based, plain-English money guidance for newcomers and Canadian families. No sales pitch.
          </p>
          <div className="flex gap-4 pt-2">
            {SOCIAL.map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`social-${label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-[#A8C5B5] hover:text-[#D4A373] transition-colors"
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>

        {/* Topics */}
        <div className="space-y-4">
          <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373]">Topics</h4>
          <ul className="space-y-2">
            {TOPICS.map((t) => (
              <li key={t.href}>
                <Link
                  to={t.href}
                  className="text-[#A8C5B5] hover:text-white text-sm transition-colors"
                >
                  {t.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Newsletter */}
        <div className="space-y-4">
          <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373]">
            Newsletter
          </h4>
          <p className="text-[#A8C5B5] text-sm leading-relaxed">
            One careful email, twice a month. No spam, no affiliate gimmicks.
          </p>
          {status ? (
            <p
              data-testid="footer-subscribe-status"
              className={`text-sm font-medium ${
                status.type === "success" ? "text-[#D4A373]" : "text-red-300"
              }`}
            >
              {status.msg}
            </p>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col gap-2" data-testid="footer-subscribe-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                required
                data-testid="footer-email-input"
                className="bg-[#2D6A4F] border border-[#40916C] text-white placeholder-[#A8C5B5] px-4 py-2.5 rounded-md text-sm focus:outline-none focus:border-[#D4A373] transition-colors"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-subscribe-btn"
                className="bg-[#D4A373] hover:bg-[#E6B88A] text-[#1B4332] font-semibold text-sm px-4 py-2.5 rounded-md transition-colors disabled:opacity-60"
              >
                {loading ? "Subscribing..." : "Subscribe"}
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="border-t border-[#2D6A4F] max-w-screen-xl mx-auto px-6 py-5 flex flex-col sm:flex-row justify-between items-center gap-2">
        <p className="text-[#A8C5B5] text-xs">
          © {new Date().getFullYear()} Straight Talk Money with Michael. All rights reserved.
        </p>
        <div className="flex gap-4">
          <Link to="/contact" className="text-[#A8C5B5] hover:text-white text-xs transition-colors">Contact</Link>
          <Link to="/about" className="text-[#A8C5B5] hover:text-white text-xs transition-colors">About</Link>
        </div>
      </div>
    </footer>
  );
}
